// Дашборд (Справки → dashboard).
//
// Подредбата е по важност отгоре надолу: показатели за периода → кой прави
// оборота (сервитьори · станции · артикули) → какво иска внимание → и чак
// най-долу графиката по дни, която е контекст, не действие.
//
// Всичко идва от ЕДИН endpoint (`GET /reports/dashboard`) и е зад ЕДНО право
// (`view_dashboard`). Съседните справки имат свои права — дърпането им тук би
// счупило дашборда за човек, който има достъп само до него.
//
// ДВАТА РЕЖИМА НА ВИДИМОСТ: без втори код (`session_access: false`) BE-то
// крие закритите В БРОЙ без бон („Закриване на сметка(1)") от целия отговор.
// Тогава числата са без тях и това се казва изрично, а разбивката
// фискализирани/нефискализирани, която без тях не е пълна, не се рисува.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useDashboard } from "../../../hooks/useReports";
import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { parseDecimal } from "../../../components/dataTable";
import { getBillHistoryById } from "../../../api/reportsBillsHistory";
import type { BillHistory } from "../../../api/reportsBillsHistory";
import BillOperationsModal from "./BillOperationsModal";
import { ReportTopBar } from "./ReportTopBar";
import { useReportLink } from "./useReportLink";
import { DashboardKpis } from "./dashboard/DashboardKpis";
import { WaitersRevenueCard } from "./dashboard/WaitersRevenueCard";
import { StationsShareCard } from "./dashboard/StationsShareCard";
import { TopItemsCard } from "./dashboard/TopItemsCard";
import { DashboardAlertsCard } from "./dashboard/DashboardAlertsCard";
import { PaymentMixCard } from "./dashboard/PaymentMixCard";
import { RevenueDaysCard } from "./dashboard/RevenueDaysCard";
import {
  businessDateOf,
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

type DashboardReportProps = {
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
};

export const DashboardReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: DashboardReportProps = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const navigate = useNavigate();
  const langCode = i18n.language?.startsWith("en") ? "en" : "bg";

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [selectedBill, setSelectedBill] = useState<BillHistory | null>(null);

  const filters = useMemo(() => {
    const next: {
      language_code: string;
      start_date?: string;
      end_date?: string;
      compare: boolean;
      include_heatmap: boolean;
      include_comparison: boolean;
    } = {
      language_code: langCode,
      compare,
      // Дашбордът вече не рисува heatmap — не се плаща групиране по 8 дни
      // при всяка смяна на периода.
      include_heatmap: false,
      // Нито заварените агрегати „днес до същия час срещу вчера" — Δ-тата
      // идват от `previous` за ИЗБРАНИЯ период.
      include_comparison: false,
    };
    if (dateRange?.[0] && dateRange?.[1]) {
      next.start_date = dateRange[0].format("YYYY-MM-DD HH:mm:ss");
      next.end_date = dateRange[1].format("YYYY-MM-DD HH:mm:ss");
    }
    return next;
  }, [langCode, compare, dateRange]);

  const { data, isLoading, isError, isFetching, refetch } =
    useDashboard(filters);
  const { data: paymentMethods = [] } = usePaymentMethods();

  // „Отворени сега" е състояние в момента — има смисъл само когато периодът
  // включва днес; иначе би застанало число от друг ден до числа от периода.
  const includesToday = useMemo(() => {
    if (!dateRange?.[1]) return true;
    // По РАБОТНА дата: краят на вчерашния работен ден е 05:59 ДНЕС, така че
    // суровото сравнение по календарен ден обявява „вчера" за днес и рисува
    // „Открити" до числа от миналото.
    return !businessDateOf(dateRange[1]).isBefore(businessDateOf(dayjs()), "day");
  }, [dateRange]);

  // Сигналът се отваря като сметка — същият модал като в „История на маси".
  const openBill = async (billId: number) => {
    const bill = await getBillHistoryById(billId, "reports");
    if (bill) setSelectedBill(bill);
  };

  const canOpenBills = useReportLink("bills-history") !== null;

  const revenue = data ? parseDecimal(data.closed_today.total_amount) : 0;

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.dashboardTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          {isError && !data ? (
            // Без този клон грешка в заявката оставяше вечен spinner: `isLoading`
            // пада, `data` остава undefined и екранът се върти до презареждане.
            <div className="grid place-items-center gap-3 rounded-[14px] border border-shade-2 bg-surface px-5 py-16 text-center">
              <p className="text-[14px] font-bold text-soft">
                {t("reportsPage.dashLoadFailed")}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="h-9 rounded-lg border border-shade-3 bg-surface px-4 text-[12.5px] font-bold text-ink transition-colors hover:bg-fill"
              >
                {t("reportsPage.refresh")}
              </button>
            </div>
          ) : isLoading || !data ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <div
              className={`flex flex-col gap-3.5 transition-opacity ${
                isFetching ? "opacity-60" : ""
              }`}
            >
              <DashboardKpis
                data={data}
                compare={compare}
                includesToday={includesToday}
                isNarrow={narrow}
              />

              <div
                className={`grid gap-3.5 ${
                  narrow
                    ? "grid-cols-1"
                    : "grid-cols-1 lg:grid-cols-[1.1fr_1fr_1.15fr]"
                }`}
              >
                <WaitersRevenueCard
                  waiters={data.waiters ?? []}
                  totalRevenue={revenue}
                />
                <StationsShareCard stations={data.stations_today} />
                <TopItemsCard items={data.top_items} />
              </div>

              <div
                className={`grid gap-3.5 ${
                  narrow ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[1.45fr_1fr]"
                }`}
              >
                <DashboardAlertsCard
                  alerts={data.alerts ?? []}
                  onOpenBill={openBill}
                />
                <PaymentMixCard
                  closed={data.closed_today}
                  paymentMethods={paymentMethods}
                />
              </div>

              <RevenueDaysCard
                days={data.daily_reports}
                periodStart={data.period_start}
                periodEnd={data.period_end}
                // Клик по стълбче отваря деня в „История на маси" — само ако
                // човекът има правото за нея.
                onPickDay={
                  canOpenBills
                    ? (date) => navigate(`/reports/bills-history?date=${date}`)
                    : undefined
                }
                isNarrow={narrow}
              />
            </div>
          )}
        </div>
      </div>

      {selectedBill ? (
        <BillOperationsModal
          bill={selectedBill}
          open
          onClose={() => setSelectedBill(null)}
          onBillUpdated={() => refetch()}
          // БЕЗ `showCancelButton`: тази справка е за четене. Футърът с
          // „Сторнирай всичко", „Възстанови" и „Фактуриране" се показва само в
          // „История на маси", където правото е `view_tables_history`.
        />
      ) : null}
    </div>
  );
};
